import { useEffect, useRef, useState } from 'react';
import type { AvailabilityResponseModel, AvailabilitySlotModel } from '../../Core/Models/BookingModels';
import CalendarService from '../../Core/Services/Calendar.service';
import { CalendarComponent } from '../Calendar/Component';
import { calEvents, type CalEventId } from '../../Core/Constants/CalEvents';
import './cal-booking.css';

export function studioMonth(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Detroit', year: 'numeric', month: '2-digit' }).format(date);
}
export function shiftMonth(month: string, offset: number) {
  const [year, number] = month.split('-').map(Number);
  return new Date(Date.UTC(year, number - 1 + offset, 1)).toISOString().slice(0, 7);
}
export function calCheckoutUrl(slug: string, isoStart: string) {
  const url = new URL(`https://cal.com/cmjcinema/${slug}`);
  // Cal.com's booker uses `slot` to open the details/payment step directly.
  url.searchParams.set('slot', new Date(isoStart).toISOString());
  return url.toString();
}

export function CalBooking({ event, theme = 'studio', navigate = (url: string) => window.location.assign(url) }: {
  event: CalEventId; theme?: 'campaign' | 'studio'; navigate?: (url: string) => void;
}) {
  const offer = calEvents[event];
  const [month, setMonth] = useState(() => studioMonth());
  const [availability, setAvailability] = useState<AvailabilityResponseModel | null>(null);
  const [activeDate, setActiveDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlotModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const pending = useRef(false);
  const generation = useRef(0);
  const currentMonth = studioMonth();

  useEffect(() => {
    const controller = new AbortController();
    generation.current += 1;
    pending.current = false;
    setChecking(false);
    setLoading(true); setError(''); setAvailability(null); setSelectedSlot(null); setActiveDate('');
    void CalendarService.getAvailability(undefined, { event, month }, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      if (result.ok) {
        setAvailability(result.data);
        setActiveDate(result.data.days.find(day => day.isAvailable)?.date || '');
      } else setError(result.error);
      setLoading(false);
    });
    return () => { controller.abort(); generation.current += 1; };
  }, [event, month, revision]);

  async function continueToPayment() {
    if (!selectedSlot || pending.current) return;
    pending.current = true; setChecking(true); setError('');
    const version = generation.current;
    const result = await CalendarService.getAvailability(undefined, { event, month });
    if (version !== generation.current) return;
    if (result.ok) {
      setAvailability(result.data);
      const stillAvailable = result.data.days.some(day => day.slots.some(slot => slot.isoStart === selectedSlot.isoStart && slot.durationMinutes === selectedSlot.durationMinutes));
      if (stillAvailable && Date.parse(selectedSlot.isoStart) > Date.now()) {
        navigate(calCheckoutUrl(offer.slug, selectedSlot.isoStart));
      } else {
        setSelectedSlot(null);
        setError('That time is no longer available. Please choose another time.');
      }
    } else {
      setSelectedSlot(null); setAvailability(null); setError(result.error);
    }
    pending.current = false; setChecking(false);
  }

  return <div className={theme === 'campaign' ? 'tim-booking' : 'cal-booking'} aria-label={`${offer.name} booking`}>
    <p className="tim-booking-summary"><strong>{offer.startingPrice ? 'From ' : ''}${offer.price.toLocaleString('en-US')} USD</strong> · {offer.duration}</p>
    <p>{offer.startingPrice ? `Cal.com collects $${offer.price.toLocaleString('en-US')} USD for the base session. Additional scope is quoted separately.` : 'Paid in full at booking, with no deposit.'}</p>
    {event === 'custom-campaign-build' && <p>Online booking reserves a 5-hour production block. Contact the studio before booking if your project needs extra time, locations, or crew.</p>}
    <p>Choose your session time, then complete your details and payment on Cal.com. Your booking is confirmed there after successful payment.</p>
    <div className="tim-month-nav">
      <button type="button" aria-label="Previous month" disabled={month <= currentMonth || checking} onClick={() => setMonth(shiftMonth(month, -1))}>←</button>
      <span aria-live="polite">{new Date(`${month}-01T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' })}</span>
      <button type="button" aria-label="Next month" disabled={month >= shiftMonth(currentMonth, 12) || checking} onClick={() => setMonth(shiftMonth(month, 1))}>→</button>
    </div>
    <p className="tim-calendar-zone">All times are Detroit time (America/Detroit).</p>
    <CalendarComponent activeDate={activeDate} availability={availability} isLoading={loading}
      selectedSlotId={selectedSlot?.id || ''} selectedTime={selectedSlot?.label || ''}
      onSelectDate={date => { if (!checking) { setActiveDate(date); setSelectedSlot(null); setError(''); } }}
      onSelectSlot={slot => { if (!checking) { setSelectedSlot(slot); setError(''); } }} />
    {!loading && availability && !availability.days.some(day => day.isAvailable) && <p role="status">No openings this month. Try the next month or contact the studio.</p>}
    {error && <p className="tim-form-error" role="alert">{error}</p>}
    <button className="tim-button tim-button-dark" type="button" disabled={loading || checking || !selectedSlot}
      onClick={() => void continueToPayment()}>{checking ? 'Checking your time…' : 'Continue to payment'} <span aria-hidden="true">↗</span></button>
    <p className="tim-form-note">Selecting a time does not reserve it. Cal.com checks availability again when you book and handles payment securely through Stripe.</p>
    <div className="tim-booking-actions"><button type="button" disabled={loading || checking} onClick={() => setRevision(value => value + 1)}>Refresh times</button><a href={`https://cal.com/cmjcinema/${offer.slug}`} rel="noreferrer">Check directly on Cal.com ↗</a></div>
  </div>;
}
